import { clearBrowserCookies, clearWebStorage } from '@/lib/storage/browserStorage';
import { NEXT_PUBLIC_META_PIXEL_DISABLED, NEXT_PUBLIC_META_PIXEL_ID } from '@/lib/env';
import logger from './logger';

// Meta Pixel first-party cookies (_fbp, _fbc) and pixel storage keys (fbm_*). Matched by prefix so withdraw teardown clears the full set, kept disjoint from other services' key-sets (Snapchat sc_*/_scid*, GTM _ga*/_gcl*, Amplitude AMP_*, Hotjar _hj*). The `fr` cookie is owned by the
// Facebook login SDK and is intentionally excluded.
const META_PIXEL_KEY_PREFIXES = ['_fbp', '_fbc', 'fbm_'];

let enabled = false;

export const isMetaPixelConfigured = (): boolean =>
  !NEXT_PUBLIC_META_PIXEL_DISABLED && Boolean(NEXT_PUBLIC_META_PIXEL_ID?.trim());

export const isMetaPixelEnabled = (): boolean => enabled && isMetaPixelConfigured();

const isMetaPixelKey = (key: string): boolean =>
  META_PIXEL_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));

const clearMetaPixelStorage = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const cookiesRemoved = clearBrowserCookies(isMetaPixelKey);
  const localRemoved = clearWebStorage(() => window.localStorage, isMetaPixelKey);
  const sessionRemoved = clearWebStorage(() => window.sessionStorage, isMetaPixelKey);

  logger.debug('Cleared Meta Pixel storage', { cookiesRemoved, localRemoved, sessionRemoved });
};

export const enableMetaPixel = (): boolean => {
  if (typeof window === 'undefined' || !isMetaPixelConfigured()) {
    enabled = false;
    return false;
  }

  enabled = true;

  if (typeof window.fbq === 'function') {
    window.fbq('consent', 'grant');
  }

  return true;
};

export const disableMetaPixel = (): void => {
  enabled = false;

  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('consent', 'revoke');
  }

  // Mirrors Snapchat/Convert/Hotjar teardown: storage is cleared and event calls are gated off.
  // A loaded vendor runtime may persist until a full page reload.
  logger.debug('Meta Pixel disabled: stopping tracking calls and clearing storage');
  clearMetaPixelStorage();
};
